import styled from '@emotion/styled';
import { Box, Center, Grid, Group, Stack, Text, Tooltip, useMantineTheme } from '@mantine/core';
import { PieChart, PieChartSettings } from '@root/Components/Charts/PieChart';
import { FillerSwitch } from '@root/Design/Filler';
import { AnchorButton } from '@root/Design/Primitives';
import { useDi, useDiContainer } from '@root/Services/DI';
import { useEventValue } from '@root/Services/EventEmitter';
import { FormatService } from '@root/Services/FormatService';
import { ShowbackPersistence } from '@root/Services/Invoices/ShowbackService';
import { useEffect, useMemo } from 'react';
import { Settings } from 'tabler-icons-react';
import { ShowbackModel } from '../Models';
import { ActionCard, MiniActionCard, PieKeyCard } from './Components';
import { ShowbackSummaryModel } from './ShowbackSummaryModel';
import { SummarySankeyChart } from './SummarySankeyChart';
import { SummaryScoreboard } from './SummaryScoreboard';
import { SummaryStatList } from './SummaryStatList';

export function ShowbackSummary({ model }: { model: ShowbackModel }) {
    const showbackSvc = useDi(ShowbackPersistence);
    const dimName = showbackSvc.getDimensionName(model.selectedAllocationDimension);

    const container = useDiContainer();
    const summaryModel = useMemo(() => container.resolve(ShowbackSummaryModel).setSbModel(model), [model]);
    useEffect(() => summaryModel.init(), [summaryModel]);

    return (
        <SummaryLayout>
            <Stack sx={{ minWidth: 600 }}>
                {/* <MiniActionCard title="Invoice Spend Allocation">
                    <InvoiceSpendAllocation summaryModel={summaryModel} />
                </MiniActionCard> */}
                <ActionCard>
                    <Group>
                        <Tooltip withinPortal label="View Showback Settings">
                            <span>
                                <AnchorButton icon={<Settings size={18} />} text={dimName} onClick={model.editDimensionSettings} size="md" />
                            </span>
                        </Tooltip>
                        <Text>Allocation Summary</Text>
                    </Group>
                    <SummaryStatList summaryModel={summaryModel} />
                </ActionCard>
            </Stack>
            <Stack sx={{ flex: 1 }}>
                <SummaryScoreboard summaryModel={summaryModel} />
                <Grid columns={3}>
                    <Grid.Col md={3}>
                        <ActionCard title="Showback Allocation">
                            <Center sx={{ height: 500 }}>
                                <SummarySankeyChart summaryModel={summaryModel} />
                            </Center>
                        </ActionCard>
                    </Grid.Col>
                </Grid>
            </Stack>
        </SummaryLayout>
    );
}

const SummaryLayout = styled.div`
    display: grid;
    grid-template-columns: 600px minmax(500px, 1fr);
    column-gap: ${(p) => p.theme.spacing.md}px;
`;

export function InvoiceSpendAllocation({ summaryModel }: { summaryModel: ShowbackSummaryModel }) {
    const summaryData = useEventValue(summaryModel.dataChanged);
    const loading = useEventValue(summaryModel.loading);

    const data = useMemo(() => {
        if (!summaryData?.stats) {
            return undefined;
        }
        const { untagged, total } = summaryData.stats;
        if (!total) {
            return undefined;
        }
        const covered = total - untagged;
        const uncovered = Math.abs(untagged);
        const totalForPct = covered + uncovered;
        const coveredPct = covered / totalForPct;
        const uncoveredPct = uncovered / totalForPct;
        return {
            covered,
            uncovered,
            coveredPct,
            uncoveredPct,
            values: [
                { id: 'Allocated', value: covered },
                { id: 'Unallocated', value: uncovered },
            ],
        };
    }, [summaryData]);

    const theme = useMantineTheme();
    const allocColor = theme.colors.success[4];
    const unallocColor = theme.colors.warning[5];

    return (
        <Box sx={{ height: 100, paddingBottom: '10px' }}>
            <FillerSwitch loading={!!loading} noData={!data}>
                {() => (
                    <>
                        <Grid columns={2} align="center" gutter="xs">
                            <Grid.Col xs={1}>
                                <PieKeyCard
                                    label="Allocated"
                                    amount={data!.covered}
                                    color={allocColor}
                                    pct={data!.coveredPct}
                                    moneyFormat={summaryModel.showbackModel.getMoneyFormatUserPref()}
                                />
                            </Grid.Col>
                            <Grid.Col xs={1}>
                                <PieKeyCard
                                    label="Unallocated"
                                    amount={data!.uncovered}
                                    color={unallocColor}
                                    pct={data!.uncoveredPct}
                                    moneyFormat={summaryModel.showbackModel.getMoneyFormatUserPref()}
                                />
                            </Grid.Col>
                        </Grid>
                    </>
                )}
            </FillerSwitch>
        </Box>
    );
}
